import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, ModalBody, Spinner } from "reactstrap";

import { criarBaseline, executarBid, gerarPacote, listarExecucoes, obterBid } from "services/bidFreteApi";
import AbaBaseline from "./abas/AbaBaseline";
import AbaPacote from "./abas/AbaPacote";
import AbaConvites from "./abas/AbaConvites";
import AbaRanking from "./abas/AbaRanking";
import AbaNotas from "./abas/AbaNotas";
import AbaAdjudicacao from "./abas/AbaAdjudicacao";
import AbaEtapas from "./abas/AbaEtapas";
import {
  ETAPA_STATUS_UI,
  ETAPA_UI,
  EXECUCAO_UI,
  PRESET_UI,
  SITUACAO_UI,
  acoesDisponiveis,
  ehRfp,
  erroDaApi,
  etapaAtualDe,
  etapasDoPreset,
  fmtSiglas,
  ufsDoBid,
  execucaoAtiva,
  execucaoMaisRecente,
  fmtNumero,
  fmtPeriodo,
  progressoExecucao,
  temExecucaoAtiva,
} from "./regras";

// Detalhe do BID (H2/H3/H4/H8, épico PO1-2413): modal no padrão dos Leilões
// com o trilho das etapas (estado + progresso por polling de 5 s em
// GET /execucoes enquanto houver etapa assíncrona rodando), os botões Criar
// baseline / Gerar pacote / Executar conforme a etapa e as dependências, e as
// abas. Abre por ?abrir=<id> na lista.
//
// Abas: acrescente/troque a entrada
// em `ABAS`. Cada aba recebe { bid, empresaId, execucoes, recarregar, onChanged }.

const POLL_MS = 5000;

export const ABAS = [
  { key: "baseline", label: "Baseline", component: AbaBaseline, visivel: () => true },
  { key: "pacote", label: "Pacote", component: AbaPacote, visivel: (bid) => ehRfp(bid.preset) },
  { key: "convites", label: "Convites e propostas", component: AbaConvites, visivel: (bid) => ehRfp(bid.preset) },
  { key: "ranking", label: "Ranking", component: AbaRanking, visivel: () => true },
  { key: "notas", label: "Notas", component: AbaNotas, visivel: () => true },
  { key: "adjudicacao", label: "Vencedoras", component: AbaAdjudicacao, visivel: (bid) => ehRfp(bid.preset) },
  { key: "etapas", label: "Etapas", component: AbaEtapas, visivel: () => true },
];

function Badge({ tone, children, title }) {
  return (
    <span className={`lf-badge lf-badge-${tone || "neutral"}`} title={title}>
      {children}
    </span>
  );
}

export default function BidDetalhe({ isOpen, onClose, empresaId, bidId, onChanged }) {
  const [bid, setBid] = useState(null);
  const [execucoes, setExecucoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [aba, setAba] = useState("baseline");
  const [busy, setBusy] = useState(false);
  const [acaoErro, setAcaoErro] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // 'baseline' | 'pacote' | 'executar'
  const ativasRef = useRef(false);

  const carregar = useCallback(
    async (silencioso) => {
      if (!empresaId || !bidId) return;
      if (!silencioso) setLoading(true);
      try {
        const [b, ex] = await Promise.all([obterBid(empresaId, bidId), listarExecucoes(empresaId, bidId).catch(() => [])]);
        setBid(b);
        const lista = Array.isArray(ex) ? ex : (ex && ex.items) || [];
        setExecucoes(lista.length || !b || !Array.isArray(b.execucoes) ? lista : b.execucoes);
        setErro(null);
      } catch (err) {
        setErro(erroDaApi(err, "Não foi possível carregar o BID.").mensagem);
      } finally {
        if (!silencioso) setLoading(false);
      }
    },
    [empresaId, bidId],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    setBid(null);
    setExecucoes([]);
    setErro(null);
    setAcaoErro(null);
    setConfirmar(null);
    setAba("baseline");
    carregar(false);
    return undefined;
  }, [isOpen, carregar]);

  // Polling de 5 s só com etapa assíncrona rodando e a aba visível; quando a
  // última execução termina, recarrega o cabeçalho (totais e etapas).
  const ativas = temExecucaoAtiva(execucoes);
  useEffect(() => {
    if (ativasRef.current && !ativas) carregar(true);
    ativasRef.current = ativas;
  }, [ativas, carregar]);
  useEffect(() => {
    if (!isOpen || !ativas || !empresaId || !bidId) return undefined;
    const p = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      listarExecucoes(empresaId, bidId)
        .then((ex) => setExecucoes(Array.isArray(ex) ? ex : (ex && ex.items) || []))
        .catch(() => {});
    }, POLL_MS);
    return () => clearInterval(p);
  }, [isOpen, ativas, empresaId, bidId]);

  const acoes = useMemo(() => acoesDisponiveis(bid, execucoes), [bid, execucoes]);
  const etapas = bid ? etapasDoPreset(bid.preset) : [];
  const atual = bid ? etapaAtualDe(bid) : null;
  const abasVisiveis = bid ? ABAS.filter((a) => a.visivel(bid)) : [];
  const AbaAtiva = (abasVisiveis.find((a) => a.key === aba) || abasVisiveis[0] || {}).component;

  // 202 { execucao }: a execução nova entra no trilho na hora (o polling segue).
  const executar = async (fn, fallback, qual) => {
    if (busy) return;
    setBusy(true);
    setAcaoErro(null);
    try {
      const r = await fn();
      setConfirmar(null);
      if (r && r.execucao) setExecucoes((cur) => [r.execucao].concat((cur || []).filter((x) => x.execId !== r.execucao.execId)));
      await carregar(true);
      if (onChanged) onChanged();
    } catch (err) {
      const e = erroDaApi(err, fallback);
      // 409 baseline_existente (precisaConfirmar): pede confirmação e reenvia com { confirmar: true }.
      if (qual === "baseline" && e.status === 409 && e.precisaConfirmar) {
        setConfirmar("baseline");
      } else {
        setAcaoErro(e.errors ? `${e.mensagem} ${Object.values(e.errors).join(" ")}` : e.mensagem);
      }
    } finally {
      setBusy(false);
    }
  };

  const disparar = (qual, confirmado) => {
    if (qual === "baseline")
      return executar(() => criarBaseline(empresaId, bidId, { confirmar: Boolean(confirmado) || acoes.baseline.recriar }), "Não foi possível criar a baseline.", "baseline");
    if (qual === "pacote") return executar(() => gerarPacote(empresaId, bidId), "Não foi possível gerar o pacote.");
    return executar(() => executarBid(empresaId, bidId), "Não foi possível executar a simulação.");
  };
  const pedir = (qual, recriar) => {
    if (recriar) setConfirmar(qual);
    else disparar(qual);
  };

  const statusEtapa = (etapa) => {
    const e = ((bid && bid.etapas) || []).find((x) => x.etapa === etapa);
    return (e && e.status) || "PENDENTE";
  };
  const sit = bid ? SITUACAO_UI[bid.situacao] || SITUACAO_UI.RASCUNHO : null;

  const textoConfirmar = {
    baseline: "Recriar a baseline congela as NFs de novo e marca o pacote e a simulação como desatualizados. Continuar?",
    pacote: "Gerar o pacote de novo substitui as planilhas anteriores. Continuar?",
    executar: "Executar de novo substitui o resultado da simulação (sem duplicar). Continuar?",
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" modalClassName="modal-align-top" contentClassName="lf-modal" backdrop="static" fade={false}>
      <div className="lf-head">
        <span className="lf-head-title">
          <span>{bid ? `BID #${bid.id} · ${bid.nome}` : "BID de Frete"}</span>
          {bid ? <Badge tone={sit.tone}>{sit.label}</Badge> : null}
          {bid ? <Badge tone={ehRfp(bid.preset) ? "info" : "neutral"}>{PRESET_UI[bid.preset] ? PRESET_UI[bid.preset].label : bid.preset}</Badge> : null}
        </span>
        <button type="button" className="lf-head-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>

      <ModalBody style={{ maxHeight: "78vh", overflowY: "auto" }}>
        {loading && !bid ? (
          <div className="lf-empty-state">
            <Spinner size="sm" /> Carregando...
          </div>
        ) : erro && !bid ? (
          <div className="lf-aviso lf-aviso-danger">{erro}</div>
        ) : bid ? (
          <>
            <div className="lf-muted" style={{ marginBottom: 10 }}>
              {bid.embarcadorFantasia ? `${bid.embarcadorFantasia} · ` : ""}Emissão {fmtPeriodo(bid.dataEmissaoDe, bid.dataEmissaoAte)} · destino {fmtSiglas(ufsDoBid(bid))}
              {bid.baselineEm && bid.qtdNf != null ? ` · ${fmtNumero(bid.qtdNf)} NFs congeladas` : ""}
            </div>

            <div className="bf-trilho" data-testid="bf-trilho">
              {etapas.map((etapa, i) => {
                const status = statusEtapa(etapa);
                const st = ETAPA_STATUS_UI[status] || ETAPA_STATUS_UI.PENDENTE;
                const ui = ETAPA_UI[etapa];
                const exec = ui.tipo === "assincrona" ? execucaoMaisRecente(execucoes, etapa) : null;
                const rodando = execucaoAtiva(exec);
                const eu = exec ? EXECUCAO_UI[exec.situacao] : null;
                const prog = exec ? progressoExecucao(exec) : null;
                return (
                  <div key={etapa} className={`bf-etapa${etapa === atual ? " is-atual" : ""}`} data-etapa={etapa} data-status={status}>
                    <div className="bf-etapa-ordem">Etapa {i + 1}</div>
                    <div className="bf-etapa-nome">{ui.label}</div>
                    <div className={`bf-etapa-status tone-${rodando && eu ? eu.tone : st.tone}`}>{rodando && eu ? eu.label : exec && exec.situacao === "FALHA" && status !== "CONCLUIDA" ? "Falhou" : st.label}</div>
                    {exec && (rodando || exec.situacao !== "CONCLUIDO") ? (
                      <div className="bf-progress" title={`${Math.round((prog || 0) * 100)}%`}>
                        <div className={`bf-progress-bar${eu && !rodando ? ` tone-${eu.tone}` : ""}`} style={{ width: `${Math.round((prog || 0) * 100)}%` }} />
                      </div>
                    ) : null}
                    {rodando && exec.itensTotal ? (
                      <div className="lf-muted" style={{ fontSize: 11 }}>
                        {fmtNumero(exec.itensFeitos)} / {fmtNumero(exec.itensTotal)}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="bf-acoes">
              <Button color="primary" className="btn-sm" disabled={!acoes.baseline.habilitado || busy} title={acoes.baseline.motivo || ""} onClick={() => pedir("baseline", acoes.baseline.recriar)}>
                {acoes.baseline.rodando ? <Spinner size="sm" /> : acoes.baseline.recriar ? "Recriar baseline" : "Criar baseline"}
              </Button>
              {ehRfp(bid.preset) ? (
                <Button color="info" className="btn-sm" outline disabled={!acoes.pacote.habilitado || busy} title={acoes.pacote.motivo || ""} onClick={() => pedir("pacote", acoes.pacote.recriar)}>
                  {acoes.pacote.rodando ? <Spinner size="sm" /> : acoes.pacote.recriar ? "Gerar pacote de novo" : "Gerar pacote"}
                </Button>
              ) : null}
              <Button color="success" className="btn-sm" disabled={!acoes.executar.habilitado || busy} title={acoes.executar.motivo || ""} onClick={() => pedir("executar", acoes.executar.recriar)}>
                {acoes.executar.rodando ? <Spinner size="sm" /> : acoes.executar.recriar ? "Executar de novo" : "Executar simulação"}
              </Button>
              {!acoes.executar.habilitado && acoes.executar.motivo ? <span className="lf-muted">{acoes.executar.motivo}</span> : null}
            </div>

            {confirmar ? (
              <div className="lf-aviso lf-aviso-warning">
                {textoConfirmar[confirmar]}{" "}
                <Button color="warning" size="sm" className="ml-2" onClick={() => disparar(confirmar, true)} disabled={busy}>
                  Confirmar
                </Button>{" "}
                <Button color="secondary" size="sm" outline onClick={() => setConfirmar(null)} disabled={busy}>
                  Desistir
                </Button>
              </div>
            ) : null}
            {acaoErro ? <div className="lf-aviso lf-aviso-danger">{acaoErro}</div> : null}

            <div className="bf-tabs" role="tablist">
              {abasVisiveis.map((a) => (
                <button key={a.key} type="button" role="tab" className={`bf-tab${(abasVisiveis.find((x) => x.key === aba) || abasVisiveis[0]).key === a.key ? " is-active" : ""}`} onClick={() => setAba(a.key)}>
                  {a.label}
                </button>
              ))}
            </div>

            {AbaAtiva ? <AbaAtiva bid={bid} empresaId={empresaId} execucoes={execucoes} recarregar={() => carregar(true)} onChanged={onChanged} /> : null}
          </>
        ) : null}
      </ModalBody>
    </Modal>
  );
}
